import { useState, useEffect } from "react";
import { API , authFetch } from "@/App";
import { Code, ChevronDown, ChevronRight, Search, FileText, Shield } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const METHOD_COLORS = {
  GET: "bg-emerald-50 text-emerald-700 border-emerald-200",
  POST: "bg-blue-50 text-blue-700 border-blue-200",
  PUT: "bg-amber-50 text-amber-700 border-amber-200",
  PATCH: "bg-violet-50 text-violet-700 border-violet-200",
  DELETE: "bg-red-50 text-red-700 border-red-200",
};

const ApiDocsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedCats, setExpandedCats] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await authFetch(`${API}/api-docs`, {  });
        const json = await res.json();
        if (json.success) setData(json.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const toggleCat = (cat) => setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));

  const categories = data?.categories || {};
  const filteredCategories = {};
  Object.entries(categories).forEach(([cat, routes]) => {
    const filtered = routes.filter(r =>
      !search || r.path.toLowerCase().includes(search.toLowerCase()) || r.name?.toLowerCase().includes(search.toLowerCase()) || r.description?.toLowerCase().includes(search.toLowerCase())
    );
    if (filtered.length > 0) filteredCategories[cat] = filtered;
  });

  return (
    <div className="space-y-6" data-testid="api-docs-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">API Documentation</h1>
          <p className="text-sm text-slate-500 mt-1">
            {data ? `v${data.version} · ${data.total_endpoints} endpoints · Audit: ${data.last_audit?.split("T")[0]}` : "Loading..."}
          </p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search endpoints..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-10 w-[250px]" data-testid="api-search" />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Total Endpoints</p>
          <p className="text-2xl font-bold font-mono text-slate-900 mt-1">{data?.total_endpoints || 0}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Categories</p>
          <p className="text-2xl font-bold font-mono text-blue-600 mt-1">{Object.keys(categories).length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Version</p>
          <p className="text-2xl font-bold font-mono text-emerald-600 mt-1">{data?.version || "—"}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] uppercase text-slate-400 font-semibold">Last Audit</p>
          <p className="text-sm font-bold font-mono text-slate-700 mt-1">{data?.last_audit?.split("T")[0] || "—"}</p>
        </Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-[#8B1A1A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(filteredCategories).map(([cat, routes]) => {
            const isExpanded = expandedCats[cat] !== false;
            return (
              <Card key={cat} className="overflow-hidden" data-testid={`api-cat-${cat.toLowerCase().replace(/\s/g, '-')}`}>
                <button onClick={() => toggleCat(cat)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-2">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <span className="text-sm font-semibold text-slate-900">{cat}</span>
                    <Badge variant="outline" className="text-[10px]">{routes.length}</Badge>
                  </div>
                </button>
                {isExpanded && (
                  <div className="divide-y divide-slate-100">
                    {routes.map((r, i) => (
                      <div key={`${r.method}-${r.path}-${i}`} className="px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50 transition-colors"
                        data-testid={`api-endpoint-${r.method}-${r.path.replace(/\//g, '-')}`}>
                        <Badge className={`text-[10px] font-mono w-14 text-center ${METHOD_COLORS[r.method] || "bg-slate-100"}`}>{r.method}</Badge>
                        <code className="text-xs font-mono text-slate-700 flex-1">{r.path}</code>
                        {r.description && <span className="text-[10px] text-slate-400 truncate max-w-[300px]">{r.description}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApiDocsPage;
